import { create } from "zustand";

/**
 * Сообщения, отправленные оператором, но ещё не подтверждённые Telegram.
 *
 * Путь отправки асинхронный: POST кладёт команду в Redis, воркер имитирует
 * набор (1–3 с) и только потом реально шлёт. Без этой очереди пузырь появлялся
 * бы через несколько секунд после нажатия, и клиент казался бы зависшим.
 *
 * В localStorage не сохраняем: после перезагрузки сообщение либо уже ушло (и
 * придёт из истории), либо потеряно вместе с вкладкой — вечное «отправляется…»
 * хуже, чем его отсутствие.
 */
export interface PendingMessage {
  localId: string;
  accountId: string;
  chatId: string;
  text: string | null;
  /** Имя приложенного файла — показываем в пузыре, пока нет настоящего медиа. */
  fileName: string | null;
  mediaKind: string | null;
  replyToTgId: number | null;
  createdAt: number;
  /** Появляется после ответа API; по нему событие воркера снимает пузырь. */
  commandId: string | null;
  status: "sending" | "failed";
  error: string | null;
}

interface OutboxState {
  items: PendingMessage[];
  add: (m: Omit<PendingMessage, "createdAt" | "status" | "error" | "commandId">) => void;
  attachCommandId: (localId: string, commandId: string) => void;
  fail: (localId: string, error: string) => void;
  remove: (localId: string) => void;
  /** Снять пузырь, который воркер подтвердил (или отверг) по command_id. */
  removeByCommandId: (commandId: string) => void;
  failByCommandId: (commandId: string, error: string) => void;
}

export const useOutbox = create<OutboxState>((set) => ({
  items: [],
  add: (m) =>
    set((s) => ({
      items: [
        ...s.items,
        { ...m, createdAt: Date.now(), commandId: null, status: "sending", error: null },
      ],
    })),
  attachCommandId: (localId, commandId) =>
    set((s) => ({
      items: s.items.map((i) => (i.localId === localId ? { ...i, commandId } : i)),
    })),
  fail: (localId, error) =>
    set((s) => ({
      items: s.items.map((i) =>
        i.localId === localId ? { ...i, status: "failed" as const, error } : i
      ),
    })),
  remove: (localId) => set((s) => ({ items: s.items.filter((i) => i.localId !== localId) })),
  removeByCommandId: (commandId) =>
    set((s) => ({ items: s.items.filter((i) => i.commandId !== commandId) })),
  failByCommandId: (commandId, error) =>
    set((s) => ({
      items: s.items.map((i) =>
        i.commandId === commandId ? { ...i, status: "failed" as const, error } : i
      ),
    })),
}));
